import type { DestinationIdApiResponse } from '../../types/api/DestinationId'
import type { Destination } from '../../types/geo/Destination'
import type { CitiesRecommended, Hotel, HotelRecommended } from '../../types/hotel/Hotel'
import type { CheckinTimes } from '../../types/hotel/Room'
import type { ExtractHotelInfoPayload, RecommendedHotelListItem } from '../../types/hoteles/Recommended'
import type { StrapiDestination } from '../../types/strapi/StrapiDestination'

// Formato estrellas (Tab hoteles)
export const splitEstrellas = (textoEstrellas: string | undefined): { countEstrellas: number, rightString: string | null } => {
  const parts = textoEstrellas?.split(' ') ?? [] // dividir el string en un array
  const countEstrellas = parts[0]?.split('*')?.length ? parts[0].split('*').length - 1 : 0 // obtener la cantidad de asteriscos
  const rightString = parts[1] || null // obtener el string de la parte derecha del espacio, si existe
  return { countEstrellas, rightString }
}

// Obtener array precios
export const extractIdAndPrice = (hotels: HotelRecommended[]): Array<{ id: number, price: string }> => {
  return hotels?.map(hotel => ({
    id: hotel.id,
    price: hotel.price
  }))
}

// Obtener precio desde id
export const getPriceById = (id: number, hotels: HotelRecommended[]): string | undefined => {
  return hotels.find(hotel => hotel.id === id)?.price
}

// Obtener info hoteles
export const extractHotelInfo = (dataHoteles: ExtractHotelInfoPayload) => {
  const tabs = dataHoteles?.data?.destination?.map((item) => ({ ...item, label: item.name }))
  const stars = dataHoteles?.data?.stars
  const lista = dataHoteles?.data?.hotel?.reduce<RecommendedHotelListItem[]>((acc, hotel) => {
    const count = acc.filter(h => h.destination === hotel.destination)?.length
    if (count < 4) {
      acc.push(hotel)
    }
    return acc
  }, [])
  const precio = extractIdAndPrice(lista as unknown as HotelRecommended[])

  return { tabs, stars, lista, precio }
}

// Ordenar ciudades tabs
export const ordenarCiudades = (ciudades: CitiesRecommended[], ordenCiudades: StrapiDestination[]): CitiesRecommended[] => {
  return ciudades?.sort((a, b) => {
    const ordenA = ordenCiudades.find((o: StrapiDestination) => o.identifier === a.id)?.order ?? 0
    const ordenB = ordenCiudades.find((o: StrapiDestination) => o.identifier === b.id)?.order ?? 0

    if (ordenA < ordenB) {
      return -1
    }
    if (ordenA > ordenB) {
      return 1
    }
    return 0
  }) ?? []
}

type SearchSortComparator = (a: Partial<Hotel> | null | undefined, b: Partial<Hotel> | null | undefined) => number

// Ordenar hoteles search
export const ordenarSearch: Array<SearchSortComparator | null> = [
  null, // Sin orden (Relevancia)
  (a, b) => { // Precio (menor a mayor)
    if (!a || !b) return 0
    return primerPrecio(a) - primerPrecio(b)
  },
  (a, b) => { // Precio (mayor a menor)
    if (!a || !b) return 0
    return ultimoPrecio(b) - ultimoPrecio(a)
  },
  (a, b) => { // Estrellas (menor a mayor)
    return Number(a?.stars?.order) - Number(b?.stars?.order)
  },
  (a, b) => { // Estrellas (mayor a menor)
    return Number(b?.stars?.order) - Number(a?.stars?.order)
  }
]

// Obtener primer precio del primer pack disponible
export const primerPrecio = (hotel: Partial<Hotel> | null | undefined): number => {
  if (!hotel) { return 0 }
  const packMinPrice = hotel?.pack_min_price ?? {}
  const firstKeyWithPrice: string | undefined = Object.keys(packMinPrice).find(key => packMinPrice[key]?.price || packMinPrice[key]?.price_label)
  const firstPriceOrLabel = firstKeyWithPrice ? packMinPrice[firstKeyWithPrice] : undefined

  return firstPriceOrLabel?.price ?? 0
}

// Obtener pack hora del primer precio del primer pack disponible
export const primerPrecioPack = (hotel: Partial<Hotel> | null | undefined): string | undefined => {
  if (!hotel) { return undefined }
  const packMinPrice = hotel?.pack_min_price ?? {}
  return Object.keys(packMinPrice).find(key => {
    const slot = packMinPrice[key]
    return slot?.price || slot?.price_label
  })
}

// Obtener primer precio label del primer pack disponible
export const primerPrecioLabel = (hotel: Partial<Hotel> | null | undefined): string => {
  if (!hotel) { return '' }
  const packMinPrice = hotel?.pack_min_price ?? {}
  const firstKeyWithPrice = Object.keys(packMinPrice).find(key => {
    const slot = packMinPrice[key]
    return slot?.price || slot?.price_label
  })
  const firstPriceOrLabel = firstKeyWithPrice ? packMinPrice[firstKeyWithPrice] : undefined

  return firstPriceOrLabel?.price_label ?? ''
}

// Obtener el último precio válido de un objeto de packs
export const ultimoPrecio = (hotel: Partial<Hotel> | null | undefined): number => {
  if (!hotel) { return 0 }
  const packs = hotel.pack_min_price ?? {}
  const keys = Object.keys(packs)
    .filter(key => typeof packs[key]?.price === 'number' && !isNaN(packs[key]?.price as number))
    .sort((a, b) => Number(a) - Number(b))
  if (keys?.length === 0) return 0
  const lastKey = keys[keys?.length - 1]
  return packs[lastKey ?? '']?.price ?? 0
}

// Obtener array de horarios dado un hotel (keys: 3, 6, 24)
export const getHorasHotel = (hotel: Partial<Hotel> | null | undefined, idioma: string): Record<string, string[]> => {
  return Object.keys(hotel?.checkin_times || {}).reduce((acc: Record<string, string[]>, key: string) => {
    const slot = hotel?.checkin_times?.[key] ?? {}
    acc[key] = Object.keys(slot).filter(subKey => slot[subKey] === true).map(subKey => changeHourFormat(idioma) ? format12Hour(subKey) : subKey)
    return acc
  }, {})
}

// Obtiene el primer pack que tiene horarios disponibles
export const getFirstNonEmptyKey = (checkin_times: CheckinTimes) => {
  for (const key in checkin_times) {
    if (Object.keys(checkin_times[key] ?? {}).length > 0) {
      return key
    }
  }
  return null
}

// Diferencia entre horas
export const diffHours = (start: string, end: string): number => {
  const startDate = createDateFromString(start)
  const endDate = createDateFromString(end)
  const difference = endDate.getTime() - startDate.getTime()
  return difference / (1000 * 60 * 60)
}

export const createDateFromString = (time: string) => {
  const parts = time.split(':').map(Number)
  const h = parts[0] ?? 0
  const m = parts[1] ?? 0
  return new Date(0, 0, 0, h, m)
}

// Obtener query de url de un destination
export const getDestinationInfo = async (destination_id: number, locale: Ref): Promise<{ query: string, destinationData: Destination | null}> => {
  if (destination_id) {
    const { data: destinationData } = await useFetch<DestinationIdApiResponse>(`${useBaseUrl()}/destination-id`, {
      key: `destination-${destination_id}`,
      headers: { 'Content-Type': 'application/json' },
      query: {
        destination_id,
        locale
      },
      getCachedData: (key, nuxt) => nuxt.payload.data[key],
      onResponseError(res) {
        logError(res)
      }
    })

    if (!destinationData.value?.data) {
      console.error('Error: No se han recibido datos de /destination-id')
      return { query: '', destinationData: null }
    }

    const location = destinationData.value?.data?.location
    const [latStr, lonStr] = location ? location.split(',') : [null, null]

    const objQuery: Record<string, string | number | undefined | null> = {
      'data-destinationId': destinationData.value?.data?.element_id,
      'data-longitude': lonStr ?? undefined,
      'data-latitude': latStr ?? undefined,
      'data-distance': destinationData.value?.data?.search_distance,
      'data-search': destinationData.value?.data?.name_translated,
      'data-country': destinationData.value?.data?.country_name_translated,
      'search': destinationData.value?.data?.name_translated
    }

    return {
      query: Object.keys(objQuery).map(key => (objQuery[key] ? `${key}=${objQuery[key]}` : '')).filter(Boolean).join('&'),
      destinationData: destinationData.value?.data ?? null
    }
  }
  return { query: '', destinationData: null }
}

export const showExtraBetween = <T extends { start_time: string, end_time: string }>(ranges: Array<T>, time: string): boolean => {

  if (ranges.length === 0) return true

  const [hours, minutes] = time.split(':').map(Number)
  const givenTime = new Date()
  givenTime.setHours(hours ?? 0, minutes ?? 0, 0, 0)

  const show = ranges.some((range) => {
    const [startHours, startMinutes] = range.start_time.split(':').map(Number)
    const [endHours, endMinutes] = range.end_time.split(':').map(Number)

    const startTime = new Date()
    startTime.setHours(startHours ?? 0, startMinutes ?? 0, 0, 0)

    const endTime = new Date()
    endTime.setHours(endHours ?? 0, endMinutes ?? 0, 0, 0)

    return givenTime >= startTime && givenTime <= endTime
  })

  return show
}

export function removeCookieFileds(data: Record<string, unknown>): Record<string, unknown> {
  const newData = { ...data }
  delete newData.embeded_html // quitar parametros por tamaña de cookie
  delete newData.description
  newData.label = newData.name_translated // añadir label
  return newData
}

// Función para verificar si checkin_times está vacío
export const isCheckinTimesEmpty = (checkin_times: Record<string, Record<string, unknown>>) => {
  return Object.values(checkin_times).every((obj) => objIsEmpty(obj))
}

// Función para verificar si un objeto está vacío
export const areAllInnerObjectsEmpty = (obj: Record<string, Record<string, unknown>>): boolean => {
  return Object.values(obj).every((innerObj) => Object.keys(innerObj).length === 0)
}
